import { Link } from "@remix-run/react";
import FavoriteButton from "./FavoriteButton";
import RatingStars from "./RatingStars";
import { useRecipeMetadataForRecipe } from "~/lib/recipe-metadata-context";
import {
  sortCategoriesByPriority,
  getCategoryBadgeColor,
  getDifficultyBadgeColor,
  getDifficultyText,
  getRepresentativeCategoriesForCard,
} from "~/lib/recipe-category-utils";
import type { Recipe } from "~/lib/types";

interface RecipeCardProps {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  prepTime: number;
  cookTime: number;
  difficulty: "easy" | "medium" | "hard";
  categories: string[];
  showRating?: boolean;
}

export default function RecipeCard({
  id,
  title,
  description,
  imageUrl,
  prepTime,
  cookTime,
  difficulty,
  categories,
  showRating = true, // 기본값을 true로 변경
}: RecipeCardProps) {
  // metadata context에서 평점 정보 가져오기
  const { rating } = useRecipeMetadataForRecipe(id);

  // 카드용 대표 카테고리 계산
  const representativeCategories =
    getRepresentativeCategoriesForCard(categories);

  return (
    <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
      {/* 즐겨찾기 버튼 */}
      <div
        className="absolute top-2 right-2 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <FavoriteButton recipeId={id} />
      </div>

      <Link to={`/recipes/${id}`} className="block">
        <div className="relative h-48 bg-gray-200 dark:bg-gray-700">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700">
              <svg
                className="w-12 h-12 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-1">
              {title}
            </h3>
            <span
              className={`inline-flex justify-center items-center min-w-[44px] min-h-[44px] rounded-full text-xs font-medium ${getDifficultyBadgeColor(
                difficulty
              )}`}
            >
              {getDifficultyText(difficulty)}
            </span>
          </div>

          {/* 평점 표시 */}
          {showRating && (
            <div className="mb-2 flex justify-between py-1">
              <RatingStars
                recipeId={id}
                initialRating={rating.average || 0}
                readOnly={true}
                size="sm"
                showCount={false}
              />
              {rating.count > 0 ? (
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                  ({rating.count}개의 평가)
                </span>
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                  아직 평가가 없습니다.
                </span>
              )}
            </div>
          )}

          <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-2 mb-3">
            {description}
          </p>
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-1"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>
                {prepTime + cookTime}분 ({prepTime}분 준비)
              </span>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1">
            {representativeCategories.map((category) => (
              <span
                key={category}
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getCategoryBadgeColor(
                  category
                )}`}
              >
                {category}
              </span>
            ))}
            {categories.length > representativeCategories.length && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                +{categories.length - representativeCategories.length}
              </span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}
